import { useState, useEffect, useCallback } from 'react';
import { RaceLap } from '../../models/TelemetryData';
import { formatMilliseconds } from '../../utils/time';
import { getLapColor } from './LapSelector';

interface LapDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  laps: RaceLap[];
  selectedLapId: string;
  checkedLapIds: string[];
  loadingLapIds: string[];
  failedLapIds: string[];
  onToggleLap: (lapId: string) => void;
  allLapIds: string[];
  dropdownPos?: { top: number; left: number } | null;
}

export default function LapDrawer({
  isOpen,
  onClose,
  laps,
  selectedLapId,
  checkedLapIds,
  loadingLapIds,
  failedLapIds,
  onToggleLap,
  allLapIds,
  dropdownPos,
}: LapDrawerProps) {
  const [sortBy, setSortBy] = useState<'time' | 'lap'>('time');

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const sortedByTime = [...laps].sort((a, b) => a.time - b.time);
  const fastestLapId = sortedByTime.length > 0 ? sortedByTime[0].id : '';
  const fastestTime = sortedByTime.length > 0 ? sortedByTime[0].time : 0;

  const sortedLaps =
    sortBy === 'time'
      ? sortedByTime
      : [...laps].sort((a, b) => a.lap - b.lap);

  const handleClearAll = () => {
    checkedLapIds.forEach((id) => onToggleLap(id));
  };

  return (
    <>
      <div className="lap-dropdown-overlay" onClick={onClose} />
      <div
        className="lap-dropdown"
        style={dropdownPos ? { top: dropdownPos.top, left: dropdownPos.left } : undefined}
      >
        <div className="lap-drawer-header">
          <span>Laps</span>
          <button className="lap-drawer-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="lap-drawer-sort">
          <button
            className={`lap-drawer-sort-btn${sortBy === 'time' ? ' active' : ''}`}
            onClick={() => setSortBy('time')}
          >
            Time
          </button>
          <button
            className={`lap-drawer-sort-btn${sortBy === 'lap' ? ' active' : ''}`}
            onClick={() => setSortBy('lap')}
          >
            Lap #
          </button>
        </div>

        <div className="lap-drawer-body">
          {sortedLaps.map((lap) => {
            const isChecked = checkedLapIds.includes(lap.id);
            const isLoading = loadingLapIds.includes(lap.id);
            const isFailed = failedLapIds.includes(lap.id);
            const isFastest = lap.id === fastestLapId;
            const isSelected = lap.id === selectedLapId;
            const delta = lap.time - fastestTime;
            const isInvalid = lap.valid === false;

            return (
              <div
                key={lap.id}
                className={`lap-row${isChecked ? ' checked' : ''}${isInvalid ? ' invalid' : ''}`}
                onClick={() => onToggleLap(lap.id)}
              >
                <span className={`lap-row-check${isChecked ? ' on' : ''}`} />
                <span
                  className="lap-row-color"
                  style={{
                    backgroundColor: isChecked
                      ? getLapColor(lap.id, allLapIds)
                      : 'transparent',
                    width: '3px',
                  }}
                />
                <span className="lap-row-num">L{lap.lap}</span>
                <span className="lap-row-time">
                  {formatMilliseconds(lap.time)}
                </span>
                <span className={`lap-row-delta${isFastest ? ' neg' : ' pos'}`}>
                  {isFastest ? 'BEST' : `+${formatMilliseconds(delta)}`}
                </span>
                {isSelected && <span className="lap-row-badge">SEL</span>}
                {isFastest && <span className="lap-row-badge">FAST</span>}
                {isLoading && <span className="lap-row-badge">...</span>}
                {isFailed && <span className="lap-row-badge">ERR</span>}
              </div>
            );
          })}
        </div>

        <div className="lap-drawer-footer">
          <span>
            {checkedLapIds.length} of {laps.length} selected
          </span>
          <span className="lap-drawer-clear" onClick={handleClearAll}>
            Clear all
          </span>
        </div>
      </div>
    </>
  );
}

